import { html } from 'lit-html';
import { until } from 'lit-html/directives/until.js';
import { authService } from './services/auth.js';
import { userContextService } from './services/user-context.js';
import { subscriptionGuard } from './services/subscription-guard.js';
import { clientService } from './services/client.js';
import { cssManager } from './utils/css-manager.js';
import { navigationGuard } from './utils/navigation-guard.js';
import { navigationController } from './services/navigation-controller.js';
import { demoModeService } from './services/demo-mode.js';
import { adminService } from './services/admin.js';
import { activityTracker } from './services/activity-tracker.js';
import { logger } from './utils/logger.js';
import { t } from './i18n/index.js';
import { ga4Tracking } from './services/ga4-tracking.js';

const pageCleanups = {
  dashboard: null,
  settings: null,
  clientProfile: null,
  clients: null,
  landing: null
};

const lazyPageModules = {
  landingPage: null,
  dashboardPage: null,
  loginPage: null,
  signupPage: null,
  clientsPage: null,
  clientsEditPage: null,
  clientFormPage: null,
  clientAccountSelectionPage: null,
  clientAccountMatchingPage: null,
  clientProfilePage: null,
  clientLoginPage: null,
  settingsPage: null,
  upgradePage: null,
  downgradeSelectionPage: null,
  checkoutPage: null,
  subscriptionPage: null,
  welcomePage: null,
  privacyPolicyPage: null,
  termsOfServicePage: null,
  agencySettingsPage: null,
  restrictedPage: null,
  dashboardHeader: null,
  dataStructurePage: null,
  kommoCallbackPage: null,
  adminDashboardPage: null,
  adminTicketsPage: null,
  supportPage: null,
  blogPage: null,
  blogPostPage: null,
  emailVerificationPage: null
};

const loadingPlaceholder = html`
  <div style="min-height: 100vh; background: linear-gradient(180deg, #f8f9fa 0%, #f1f3f5 100%);">
    <style>
      [data-theme="dark"] div[style*="min-height: 100vh"] {
        background: linear-gradient(180deg, #1a1a1a 0%, #121212 100%) !important;
      }
    </style>
  </div>
`;

async function loadPageModule(moduleName) {
  if (lazyPageModules[moduleName]) {
    return lazyPageModules[moduleName];
  }

  let module;
  switch(moduleName) {
    case 'landingPage':
      module = await import('./pages/landing-page.js');
      lazyPageModules.landingPage = module.landingPage;
      // Register cleanup function
      if (module.cleanupLandingPage) {
        pageCleanups.landing = module.cleanupLandingPage;
      }
      break;
    case 'dashboardPage':
      module = await import('./pages/dashboard-page.js');
      lazyPageModules.dashboardPage = module.dashboardPage;
      // Register cleanup function
      if (module.cleanupDashboard) {
        pageCleanups.dashboard = module.cleanupDashboard;
      }
      break;
    case 'loginPage':
      module = await import('./pages/login-page.js');
      lazyPageModules.loginPage = module.loginPage;
      break;
    case 'signupPage':
      module = await import('./pages/signup-page.js');
      lazyPageModules.signupPage = module.signupPage;
      break;
    case 'clientsPage':
      module = await import('./pages/clients-page.js');
      lazyPageModules.clientsPage = module.clientsPage;
      // Register cleanup function for clients page
      if (module.resetClientsPageLoadState) {
        pageCleanups.clients = module.resetClientsPageLoadState;
      }
      break;
    case 'clientsEditPage':
      module = await import('./pages/clients-edit-page.js');
      lazyPageModules.clientsEditPage = module.clientsEditPage;
      break;
    case 'clientFormPage':
      module = await import('./pages/client-form-page.js');
      lazyPageModules.clientFormPage = module.clientFormPage;
      break;
    case 'clientAccountSelectionPage':
      module = await import('./pages/client-account-selection-page.js');
      lazyPageModules.clientAccountSelectionPage = module.clientAccountSelectionPage;
      break;
    case 'clientAccountMatchingPage':
      module = await import('./pages/client-account-matching-page.js');
      lazyPageModules.clientAccountMatchingPage = module.clientAccountMatchingPage;
      break;
    case 'clientProfilePage':
      module = await import('./pages/client-profile-page.js');
      lazyPageModules.clientProfilePage = module.clientProfilePage;
      // Register cleanup function
      if (module.cleanup) {
        pageCleanups.clientProfile = module.cleanup;
      }
      break;
    case 'clientLoginPage':
      module = await import('./pages/client-login-page.js');
      lazyPageModules.clientLoginPage = module.clientLoginPage;
      break;
    case 'settingsPage':
      module = await import('./pages/settings-page.js');
      lazyPageModules.settingsPage = module.settingsPage;
      // Register cleanup function
      if (module.cleanupSettings) {
        pageCleanups.settings = module.cleanupSettings;
      }
      break;
    case 'upgradePage':
      module = await import('./pages/upgrade-page.js');
      lazyPageModules.upgradePage = module.upgradePage;
      break;
    case 'downgradeSelectionPage':
      module = await import('./pages/downgrade-selection-page.js');
      lazyPageModules.downgradeSelectionPage = module.downgradeSelectionPage;
      break;
    case 'checkoutPage':
      module = await import('./pages/checkout-page.js');
      lazyPageModules.checkoutPage = module.checkoutPage;
      break;
    case 'subscriptionPage':
      module = await import('./pages/subscription-page.js');
      lazyPageModules.subscriptionPage = module.subscriptionPage;
      // Chamar init para resetar loading state em nova navegação
      if (module.initSubscriptionPage) {
        module.initSubscriptionPage();
      }
      break;
    case 'welcomePage':
      module = await import('./pages/welcome-page.js');
      lazyPageModules.welcomePage = module.welcomePage;
      break;
    case 'privacyPolicyPage':
      module = await import('./pages/privacy-policy.js');
      lazyPageModules.privacyPolicyPage = module.privacyPolicyPage;
      break;
    case 'termsOfServicePage':
      module = await import('./pages/terms-of-service.js');
      lazyPageModules.termsOfServicePage = module.termsOfServicePage;
      break;
    case 'helpCenterPage':
      module = await import('./pages/help-center-page.js');
      lazyPageModules.helpCenterPage = module.helpCenterPage;
      break;
    case 'documentationPage':
      module = await import('./pages/documentation-page.js');
      lazyPageModules.documentationPage = module.documentationPage;
      break;
    case 'statusPage':
      module = await import('./pages/status-page.js');
      lazyPageModules.statusPage = module.statusPage;
      break;
    case 'cookiePolicyPage':
      module = await import('./pages/cookie-policy-page.js');
      lazyPageModules.cookiePolicyPage = module.cookiePolicyPage;
      break;
    case 'agencySettingsPage':
      module = await import('./pages/agency-settings-page.js');
      lazyPageModules.agencySettingsPage = module.agencySettingsPage;
      if (module.resetAgencySettingsState) {
        pageCleanups.agencySettings = module.resetAgencySettingsState;
      }
      break;
    case 'restrictedPage':
      module = await import('./pages/restricted-page.js');
      lazyPageModules.restrictedPage = module.restrictedPage;
      break;
    case 'dashboardHeader':
      module = await import('./components/dashboard-header.js');
      lazyPageModules.dashboardHeader = module.dashboardHeader;
      break;
    case 'dataStructurePage':
      module = await import('./pages/data-structure-page.js');
      lazyPageModules.dataStructurePage = module.dataStructurePage;
      break;
    case 'kommoCallbackPage':
      module = await import('./pages/kommo-callback-page.js');
      lazyPageModules.kommoCallbackPage = module.kommoCallbackPage;
      break;
    case 'adminDashboardPage':
      module = await import('./pages/admin-dashboard-page.js');
      lazyPageModules.adminDashboardPage = module.adminDashboardPage;
      if (module.cleanupAdminDashboard) {
        pageCleanups.adminDashboard = module.cleanupAdminDashboard;
      }
      break;
    case 'adminTicketsPage':
      module = await import('./pages/admin-tickets-page.js');
      lazyPageModules.adminTicketsPage = module.adminTicketsPage;
      break;
    case 'supportPage':
      module = await import('./pages/support-page.js');
      lazyPageModules.supportPage = module.supportPage;
      break;
    case 'blogPage':
      module = await import('./pages/blog-page.js');
      lazyPageModules.blogPage = module.blogPage;
      break;
    case 'blogPostPage':
      module = await import('./pages/blog-post-page.js');
      lazyPageModules.blogPostPage = module.blogPostPage;
      break;
    case 'emailVerificationPage':
      module = await import('./pages/email-verification-page.js');
      lazyPageModules.emailVerificationPage = module.emailVerificationPage;
      break;
  }

  return lazyPageModules[moduleName];
}

class Router {
  constructor() {
    this.currentRoute = null;
    // Mapa para salvar posições de scroll por rota
    this.savedScrollPositions = new Map();
    // Última rota visitada (para detectar navegação de volta)
    this.previousPath = null;
    this.routes = {
      '/': async () => {
        if (authService.currentUser) {
          return this._redirectBasedOnSubscription();
        }
        const landingPage = await loadPageModule('landingPage');
        return landingPage();
      },
      '/landing-preview': async () => {
        // Preview da landing page sem auth check (dev only)
        const landingPage = await loadPageModule('landingPage');
        return landingPage();
      },
      '/login': async () => {
        if (authService.currentUser) {
          return this._redirectBasedOnSubscription();
        }
        const loginPage = await loadPageModule('loginPage');
        return loginPage();
      },
      '/signup': async () => {
        if (authService.currentUser) {
          return this._redirectBasedOnSubscription();
        }
        const signupPage = await loadPageModule('signupPage');
        return signupPage();
      },
      // Rotas que EXIGEM assinatura ativa
      '/dashboard': () => this.requireSubscription(async () => {
        const dashboardPage = await loadPageModule('dashboardPage');
        return dashboardPage();
      }),
      // Settings acessível mesmo sem assinatura ativa (usuário precisa gerenciar conta)
      '/settings': () => this.requireAuth(async () => {
        try {
          const settingsPage = await loadPageModule('settingsPage');
          return settingsPage();
        } catch (error) {
          logger.error('Error loading settings page:', error);
          const dashboardHeader = await loadPageModule('dashboardHeader');
          return html`
            <div class="dashboard">
              ${dashboardHeader()}
              <main class="dashboard-content">
                <div style="padding: 24px; text-align: center;">
                  <h1>${t('errors.loadSettings')}</h1>
                  <p>${t('errors.errorDetails', { message: error.message })}</p>
                  <button @click=${() => window.location.reload()}>${t('errors.reloadPage')}</button>
                </div>
              </main>
            </div>
          `;
        }
      }),
      '/agency-settings': () => this.requireSubscription(async () => {
        const agencySettingsPage = await loadPageModule('agencySettingsPage');
        return agencySettingsPage();
      }),
      '/clients': () => this.requireSubscription(async () => {
        const module = await loadPageModule('clientsPage');
        const result = module();

        // Initialize scroll-to-top button after DOM is ready
        setTimeout(() => {
          if (module.initScrollToTop) {
            module.initScrollToTop();
          }
        }, 100);

        return result;
      }),
      '/clients/new': () => this.requireSubscription(async () => {
        // Bloquear criação de clientes no modo demo
        if (demoModeService.getStatus()) {
          setTimeout(() => this.navigate('/clients'), 0);
          return html`<div>Modo demo ativo - redirecionando...</div>`;
        }
        const clientFormPage = await loadPageModule('clientFormPage');
        return clientFormPage();
      }),
      '/clients/edit': () => this.requireSubscription(async () => {
        const clientsEditPage = await loadPageModule('clientsEditPage');
        return clientsEditPage();
      }),
      '/clients/select-accounts': () => this.requireSubscription(async () => {
        // Bloquear criação de clientes no modo demo
        if (demoModeService.getStatus()) {
          setTimeout(() => this.navigate('/clients'), 0);
          return html`<div>Modo demo ativo - redirecionando...</div>`;
        }
        // Load page module (reset is handled internally by the page on first load)
        const { clientAccountSelectionPage } = await import('./pages/client-account-selection-page.js');
        return clientAccountSelectionPage();
      }),
      '/clients/match-accounts': () => this.requireSubscription(async () => {
        // Bloquear criação de clientes no modo demo
        if (demoModeService.getStatus()) {
          setTimeout(() => this.navigate('/clients'), 0);
          return html`<div>Modo demo ativo - redirecionando...</div>`;
        }
        const clientAccountMatchingPage = await loadPageModule('clientAccountMatchingPage');
        return clientAccountMatchingPage();
      }),
      '/checkout': () => this.requireAuth(async () => {
        if (demoModeService.getStatus()) {
          demoModeService.disable();
          // Re-render app so header toggle reflects disabled state
          requestAnimationFrame(() => {
            import('./main.js').then(({ renderApp }) => renderApp());
          });
        }
        const checkoutPage = await loadPageModule('checkoutPage');
        return checkoutPage();
      }),
      '/subscription': () => this.requireAuth(async () => {
        const subscriptionPage = await loadPageModule('subscriptionPage');
        // Resetar loading state para cada navegação
        if (typeof window.subscriptionPageState !== 'undefined') {
          const now = Date.now();
          const timeSinceLastNav = now - (window.subscriptionPageState.lastNavigationTime || 0);
          if (timeSinceLastNav > 500) {
            window.subscriptionPageState.isLoading = true;
            window.subscriptionPageState.lastNavigationTime = now;
          }
        }
        return subscriptionPage();
      }),
      '/verify-email': () => this.requireAuth(async () => {
        const emailVerificationPage = await loadPageModule('emailVerificationPage');
        return emailVerificationPage();
      }),
      '/welcome': () => this.requireAuth(async () => {
        const welcomePage = await loadPageModule('welcomePage');
        return welcomePage();
      }),
      '/upgrade': () => this.requireAuth(async () => {
        const upgradePage = await loadPageModule('upgradePage');
        return upgradePage();
      }),
      '/downgrade-selection': () => this.requireSubscription(async () => {
        const downgradeSelectionPage = await loadPageModule('downgradeSelectionPage');
        return downgradeSelectionPage();
      }, true), // skipClientLimitCheck = true para evitar loop infinito
      '/privacy-policy': async () => {
        const privacyPolicyPage = await loadPageModule('privacyPolicyPage');
        return privacyPolicyPage();
      },
      '/terms-of-service': async () => {
        const termsOfServicePage = await loadPageModule('termsOfServicePage');
        return termsOfServicePage();
      },
      '/help-center': async () => {
        const helpCenterPage = await loadPageModule('helpCenterPage');
        return helpCenterPage();
      },
      '/documentation': async () => {
        const documentationPage = await loadPageModule('documentationPage');
        return documentationPage();
      },
      '/status': async () => {
        const statusPage = await loadPageModule('statusPage');
        return statusPage();
      },
      '/cookie-policy': async () => {
        const cookiePolicyPage = await loadPageModule('cookiePolicyPage');
        return cookiePolicyPage();
      },
      // Suporte - abre modal sobre a pagina atual
      '/support': async () => {
        const user = authService.getCurrentUser();
        // Abrir modal apos render
        setTimeout(async () => {
          const { supportModal } = await import('./components/support-modal.js');
          supportModal.open();
        }, 200);
        if (user) {
          const dashboardPage = await loadPageModule('dashboardPage');
          return dashboardPage();
        }
        const landingPage = await loadPageModule('landingPage');
        return landingPage();
      },
      // Blog - páginas públicas para SEO
      '/blog': async () => {
        const blogPage = await loadPageModule('blogPage');
        return blogPage();
      },
      '/blog/:slug': async (params) => {
        const blogPostPage = await loadPageModule('blogPostPage');
        return blogPostPage(params.slug);
      },
      // Página de acesso restrito (para usuários com assinatura bloqueada)
      '/restricted': () => this.requireAuth(async () => {
        const restrictedPage = await loadPageModule('restrictedPage');
        return restrictedPage();
      }),
      // Página oculta de visualização da estrutura de dados (Three.js)
      // Não requer auth - é uma página de desenvolvimento/debug
      '/data-structure': async () => {
        const dataStructurePage = await loadPageModule('dataStructurePage');
        return dataStructurePage();
      },
      // Callback OAuth do Kommo - não requer auth
      '/auth/kommo/callback': async () => {
        const kommoCallbackPage = await loadPageModule('kommoCallbackPage');
        return kommoCallbackPage();
      },
      // Admin routes - hidden URL, only accessible by master admin
      '/sys-admin-x7k9m': () => this.requireMasterAdmin(async () => {
        const adminPage = await loadPageModule('adminDashboardPage');
        return adminPage();
      }),
      '/sys-admin-x7k9m/tickets': () => this.requireMasterAdmin(async () => {
        const ticketsPage = await loadPageModule('adminTicketsPage');
        return ticketsPage();
      }),
    };
  }

  get currentPath() {
    return window.location.pathname;
  }

  /**
   * Restaura a posição de scroll salva para a rota atual (usado no popstate)
   * NOTA: Para /clients, a restauração é feita pelo clients-page.js após carregar todos os dados.
   * Este método apenas retorna se há scroll pendente para restaurar.
   * @returns {boolean} true se há scroll pendente, false caso contrário
   */
  restoreScrollPosition() {
    const path = this.currentPath;

    // Para /clients, o scroll é restaurado pelo clients-page.js após o carregamento completo
    // Aqui apenas verificamos se há scroll pendente (para logging/debugging)
    if (path === '/clients') {
      const savedFrom = sessionStorage.getItem('clients_scroll_from');
      const savedPosition = sessionStorage.getItem('clients_scroll_position');

      if (savedFrom && savedFrom.startsWith('/clients/profile/') && savedPosition) {
        return true;
      }
    }

    return false;
  }

  navigate(path) {
    // Use navigation guard to prevent multiple navigations
    navigationGuard.safeNavigate(() => {
      // Only trigger transition if we're actually changing routes
      if (path !== this.currentPath) {
        const currentPath = this.currentPath;

        // Salvar posição de scroll ao sair de /clients para perfil de cliente
        // Apenas salvar se não foi previamente salvo pelo click handler (que captura antes de qualquer scroll behavior)
        if (currentPath === '/clients' && path.startsWith('/clients/profile/')) {
          const existingPosition = sessionStorage.getItem('clients_scroll_position');
          if (!existingPosition) {
            const scrollPosition = window.scrollY;
            this.savedScrollPositions.set('/clients', scrollPosition);
            sessionStorage.setItem('clients_scroll_position', String(scrollPosition));
            sessionStorage.setItem('clients_scroll_from', path);
          }
        }

        // Abort any current page loading before navigation
        navigationController.abortCurrentPage();

        // Run cleanup for the page we're leaving
        this._runPageCleanup(currentPath);

        // Dispatch custom navigation event for pages to listen
        window.dispatchEvent(new CustomEvent('app-navigation', { detail: { from: currentPath, to: path } }));

        // Salvar previousPath antes de atualizar
        this.previousPath = currentPath;

        window.history.pushState({}, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));

        // Para /clients voltando de um perfil, NÃO fazer scroll
        // O clients-page.js restaura o scroll após carregar os dados via sessionStorage
        if (path === '/clients' && currentPath.startsWith('/clients/profile/')) {
          // Limpar a posição do Map (não é mais usada, sessionStorage é suficiente)
          this.savedScrollPositions.delete('/clients');
          // Scroll será restaurado por clients-page.js após carregar dados
        } else {
          // Scroll para o topo da página em outras navegações
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
          });
        }
      }
    }, { preventDuplicate: true, delay: 500 });
  }

  /**
   * Run cleanup function for a page when navigating away
   * @param {string} path - The path we're leaving
   */
  _runPageCleanup(path) {
    try {
      if (path === '/' && pageCleanups.landing) {
        pageCleanups.landing();
      } else if (path === '/dashboard' && pageCleanups.dashboard) {
        pageCleanups.dashboard();
      } else if (path === '/settings' && pageCleanups.settings) {
        pageCleanups.settings();
      } else if (path.startsWith('/clients/profile/') && pageCleanups.clientProfile) {
        pageCleanups.clientProfile();
      } else if (path === '/clients' && pageCleanups.clients) {
        pageCleanups.clients();
      } else if (path === '/agency-settings' && pageCleanups.agencySettings) {
        pageCleanups.agencySettings();
      } else if (path.startsWith('/sys-admin-x7k9m') && pageCleanups.adminDashboard) {
        pageCleanups.adminDashboard();
      }
    } catch (error) {
      logger.warn('[Router] Error running page cleanup:', error);
    }
  }

  /**
   * Redireciona usuário autenticado baseado no status da assinatura
   * Método auxiliar para evitar duplicação de lógica
   */
  _redirectBasedOnSubscription() {
    const status = subscriptionGuard.getSubscriptionStatus();
    if (status.hasAccess) {
      this.navigate('/dashboard');
    } else if (status.status === 'pending_verification') {
      this.navigate('/verify-email');
    } else if (status.status === 'none' || status.status === 'trial_expired') {
      this.navigate('/checkout');
    } else {
      // Paused, cancelled, refund_requested, expired -> gerenciamento
      this.navigate('/subscription');
    }
    return html`<div>Redirecting...</div>`;
  }

  requireAuth(callback, isClientRoute = false) {
    if (!authService.currentUser) {
      // Se for rota de cliente, NÃO redireciona (deixa requireClientAccess tratar)
      if (isClientRoute) {
        return callback();
      }
      // Para rotas de agência, redireciona para login de agência
      setTimeout(() => this.navigate('/login'), 0);
      // Return loading state while redirecting
      return html`<div>Redirecting to login...</div>`;
    }
    return callback();
  }

  /**
   * Guard for master admin routes - requires admin privileges
   */
  async requireMasterAdmin(callback) {
    // Wait for auth to be ready before checking admin status
    const isAdmin = await adminService.isAdminAsync();

    if (!isAdmin) {
      logger.warn('[Router] Unauthorized admin access attempt:', authService.currentUser?.email);
      setTimeout(() => this.navigate('/dashboard'), 0);
      return html`<div class="loading-redirect">Access denied. Redirecting...</div>`;
    }

    return callback();
  }

  // Verifica se usuário tem assinatura ativa (paga ou via cupom)
  // Usa subscriptionGuard para verificação robusta de status
  // Considera: paused, cancelled, refund_requested, expired, trial
  // TAMBÉM verifica se excede limite de clientes
  // MODO DEMO: Se ativo, permite acesso mesmo sem assinatura
  requireSubscription(callback, skipClientLimitCheck = false) {
    if (!authService.currentUser) {
      setTimeout(() => this.navigate('/login'), 0);
      return html`<div>Redirecting to login...</div>`;
    }

    // Usar subscriptionGuard para verificação completa
    const status = subscriptionGuard.getSubscriptionStatus();

    if (!status.hasAccess) {
      if (demoModeService.getStatus()) {
        return callback();
      }

      // Determinar para onde redirecionar baseado no status
      const redirectTo = status.redirectTo || '/subscription';

      // Se está pausado, cancelado ou com reembolso solicitado -> página de assinatura
      // Se nunca teve assinatura -> checkout
      if (status.status === 'pending_verification') {
        setTimeout(() => this.navigate('/verify-email'), 0);
        return html`<div>Redirecting to verification...</div>`;
      }

      if (status.status === 'none' || status.status === 'trial_expired') {
        setTimeout(() => this.navigate('/checkout'), 0);
        return html`<div>Redirecting to checkout...</div>`;
      }

      // Para outros status (paused, refund_requested, expired, cancelled)
      // Redireciona para página de gerenciamento de assinatura
      setTimeout(() => this.navigate(redirectTo), 0);
      return html`<div>Redirecting...</div>`;
    }

    // Verifica se usuário excede o limite de clientes (apenas para rotas que não são de seleção)
    // Não verifica limite no modo demo
    if (!skipClientLimitCheck && clientService.isInitialized && !demoModeService.getStatus()) {
      const activeClients = clientService.getActiveClients();
      const activeCount = activeClients.length;
      const limitCheck = subscriptionGuard.checkClientLimitExceeded(activeCount);

      if (limitCheck.exceedsLimit) {
        setTimeout(() => this.navigate('/downgrade-selection'), 0);
        return html`<div>${t('errors.redirectingClientSelection')}</div>`;
      }
    }

    return callback();
  }

  async manageCSSForRoute(path) {
    // Track page view for analytics
    if (authService.currentUser) {
      activityTracker.trackPageView(path);
    }

    // GA4 Server-Side Page View (para todos, mesmo não autenticados)
    ga4Tracking.trackPageView(document.title, path).catch(() => {});

    // Determine which CSS to load based on route
    let cssToLoad = null;

    if ((path === '/' && !authService.currentUser) || path === '/landing-preview') {
      cssToLoad = 'landing';
    } else if (path === '/login' || path === '/signup' || path === '/verify-email') {
      cssToLoad = 'login';
    } else if (path === '/dashboard') {
      cssToLoad = 'dashboard';
    } else if (path === '/settings' || path === '/agency-settings') {
      cssToLoad = 'settings';
    } else if (path === '/clients' || path === '/clients/edit' || path === '/clients/select-accounts' || path === '/clients/match-accounts') {
      cssToLoad = 'clients';
    } else if (path === '/checkout') {
      cssToLoad = 'checkout';
    } else if (path === '/subscription') {
      cssToLoad = 'subscription';
    } else if (path.startsWith('/clients/profile/')) {
      cssToLoad = 'client-profile';
    } else if (path.startsWith('/clients/login/')) {
      cssToLoad = 'client-login';
    } else if (path === '/privacy-policy' || path === '/terms-of-service' || path === '/help-center' || path === '/documentation' || path === '/status' || path === '/cookie-policy' || path === '/support') {
      cssToLoad = 'legal';
    } else if (path.startsWith('/sys-admin-x7k9m')) {
      cssToLoad = 'admin-dashboard';
    } else {
      // Check for agency/client URL pattern: /:agencyShortId/:clientName
      const pathParts = path.split('/').filter(Boolean);
      if (pathParts.length === 2 && !path.startsWith('/clients/') && !path.startsWith('/dashboard') && !path.startsWith('/login') && !path.startsWith('/settings') && !path.startsWith('/upgrade') && !path.startsWith('/privacy') && !path.startsWith('/terms') && !path.startsWith('/blog')) {
        cssToLoad = 'client-profile';
      }
    }

    // Load CSS for current page (non-blocking)
    if (cssToLoad) {
      // Load current page CSS immediately
      const loadPromise = cssManager.loadPageCSS(cssToLoad);

      // Prefetch likely next pages in the background
      cssManager.prefetchLikelyPages(cssToLoad);

      // Wait for current page CSS to load
      await loadPromise;
    }

    // Update current route tracking
    this.currentRoute = cssToLoad || path;
  }

  requireClientAccess(clientId, callback) {
    if (demoModeService.getStatus()) {
      return callback();
    }

    // Agency users always have access to their own clients
    if (authService.currentUser && userContextService.isAgency()) {
      return callback();
    }

    const clientAuth = sessionStorage.getItem('clientAuth');

    if (clientAuth) {
      try {
        const auth = JSON.parse(clientAuth);
        const now = Date.now();
        const sessionDuration = 4 * 60 * 60 * 1000;

        if (auth.clientId === clientId && (now - auth.loginTime) < sessionDuration) {
          // Check if agency subscription was blocked (stored during login validation)
          if (auth.agencyBlocked) {
            return this._renderAgencyBlockedMessage(auth.agencyStatus);
          }

          // Trigger background check for agency subscription (non-blocking)
          this._checkAgencySubscriptionInBackground(auth);

          return callback();
        } else {
          userContextService.clearClientSession();
        }
      } catch {
        userContextService.clearClientSession();
      }
    }

    setTimeout(() => this.navigate(`/clients/login/${clientId}`), 0);
    return html`
      <div style="display: flex; justify-content: center; align-items: center; height: 100vh;">
        <div style="text-align: center;">
          <h2>${t('errors.accessDenied')}</h2>
          <p>${t('errors.redirectingClientLogin')}</p>
        </div>
      </div>
    `;
  }

  // Renders blocked message when agency subscription is inactive
  _renderAgencyBlockedMessage(status) {
    const statusMessages = {
      paused: t('clientAccess.agencyPaused') || 'A agencia pausou temporariamente os servicos.',
      expired: t('clientAccess.agencyExpired') || 'A assinatura da agencia expirou.',
      refunded: t('clientAccess.agencyRefunded') || 'A assinatura da agencia foi reembolsada.',
      suspended: t('clientAccess.agencySuspended') || 'A assinatura da agencia foi suspensa.'
    };

    return html`
      <div style="display: flex; justify-content: center; align-items: center; height: 100vh; background: var(--bg-secondary);">
        <div style="text-align: center; max-width: 400px; padding: 40px; background: var(--bg-primary); border-radius: 16px; box-shadow: var(--shadow-lg);">
          <div style="font-size: 48px; margin-bottom: 16px;">🔒</div>
          <h2 style="color: var(--text-primary); margin-bottom: 12px;">${t('clientAccess.accessBlocked') || 'Acesso Bloqueado'}</h2>
          <p style="color: var(--text-secondary); margin-bottom: 24px;">
            ${statusMessages[status] || t('clientAccess.agencyInactive') || 'A agencia esta com assinatura inativa. Entre em contato para mais informacoes.'}
          </p>
          <button
            @click=${() => window.history.back()}
            style="padding: 12px 24px; border-radius: 8px; border: 1px solid var(--border-color); background: var(--bg-secondary); cursor: pointer; font-weight: 500;"
          >
            ${t('common.goBack') || 'Voltar'}
          </button>
        </div>
      </div>
    `;
  }

  // Background check for agency subscription status (revalidates periodically)
  async _checkAgencySubscriptionInBackground(auth) {
    // Only check every 15 minutes to avoid excessive API calls
    const lastCheck = auth.lastAgencyCheck || 0;
    const checkInterval = 15 * 60 * 1000; // 15 minutes

    if (Date.now() - lastCheck < checkInterval) {
      return; // Skip check if recently validated
    }

    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { firebaseApp } = await import('./services/firebase.js');
      const functions = getFunctions(firebaseApp);

      // Call a lightweight function to check agency subscription
      const checkAgencyStatus = httpsCallable(functions, 'checkAgencySubscriptionStatus');
      const result = await checkAgencyStatus({ agencyId: auth.agencyId });

      if (result.data && !result.data.hasAccess) {
        // Update session with blocked status
        auth.agencyBlocked = true;
        auth.agencyStatus = result.data.status;
        auth.lastAgencyCheck = Date.now();
        sessionStorage.setItem('clientAuth', JSON.stringify(auth));

        // Force page reload to show blocked message
        window.location.reload();
      } else {
        // Update last check time
        auth.lastAgencyCheck = Date.now();
        sessionStorage.setItem('clientAuth', JSON.stringify(auth));
      }
    } catch {
      // Don't block access on check failure - let validateClientPassword handle it
    }
  }

  render() {
    // Show loading state while auth is initializing
    if (!authService.isInitialized) {
      return html`
        <div style="min-height: 100vh; background: linear-gradient(180deg, #f8f9fa 0%, #f1f3f5 100%);"></div>
      `;
    }

    const path = this.currentPath;

    // Manage CSS loading based on route (non-blocking)
    this.manageCSSForRoute(path).catch(() => {});

    // Detectar tipo de usuário (sempre re-detectar para garantir consistência)
    userContextService.detectUserType();

    // Verificar se a rota é permitida para o usuário atual
    if (!userContextService.isRouteAllowed(path)) {
      logger.warn('[Router] Route not allowed for current user:', path, 'User type:', userContextService.getUserType());
      const redirectUrl = userContextService.getDefaultRedirectUrl();
      setTimeout(() => this.navigate(redirectUrl), 0);
      return html`
        <div style="display: flex; justify-content: center; align-items: center; height: 100vh;">
          <div style="text-align: center;">
            <h2>${t('errors.accessDenied')}</h2>
            <p>${t('errors.redirectingProperPage')}</p>
          </div>
        </div>
      `;
    }

    // Render async route result using until directive to show loading
    return until(this._renderAsyncRoute(path), loadingPlaceholder);
  }

  async _renderAsyncRoute(path) {
    if (path.startsWith('/clients/edit/')) {
      const clientId = path.split('/clients/edit/')[1];
      const result = await this.requireAuth(async () => {
        const clientFormPage = await loadPageModule('clientFormPage');
        return clientFormPage(clientId);
      });
      return result;
    }

    if (path.startsWith('/clients/login/')) {
      const clientId = path.split('/clients/login/')[1];

      const clientLoginPage = await loadPageModule('clientLoginPage');
      return clientLoginPage(clientId);
    }

    if (path.startsWith('/clients/profile/')) {
      const clientId = path.split('/clients/profile/')[1];
      const result = await this.requireClientAccess(clientId, async () => {
        const clientProfilePage = await loadPageModule('clientProfilePage');
        return clientProfilePage(clientId);
      });
      return result;
    }

    // Blog post page - public route
    if (path.startsWith('/blog/') && path !== '/blog/') {
      const slug = path.split('/blog/')[1];
      const blogPostPage = await loadPageModule('blogPostPage');
      return blogPostPage(slug);
    }

    const pathParts = path.split('/').filter(Boolean);
    if (pathParts.length === 2 && !path.startsWith('/clients/') && !path.startsWith('/dashboard') && !path.startsWith('/login') && !path.startsWith('/settings') && !path.startsWith('/upgrade') && !path.startsWith('/privacy') && !path.startsWith('/terms') && !path.startsWith('/sys-admin') && !path.startsWith('/blog')) {
      const [, clientName] = pathParts;

      if (authService.currentUser && userContextService.isAgency()) {
        const clientProfilePage = await loadPageModule('clientProfilePage');
        return clientProfilePage(clientName);
      }

      const result = await this.requireClientAccess(clientName, async () => {
        const clientProfilePage = await loadPageModule('clientProfilePage');
        return clientProfilePage(clientName);
      });
      return result;
    }

    const route = this.routes[path];
    if (!route) {
      logger.warn('Route not found:', path);
      const defaultRoute = await this.routes['/']();
      return defaultRoute;
    }

    const result = await route();
    return result;
  }
}

export const router = new Router();